import { UIButton } from 'shared/ui';
import styles from './style.module.scss';
import { Achievement as AchievementType } from 'shared/types/api';
import { formatLargeNumber } from 'shared/utils';
import { memo, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TOKEN_ICONS } from 'shared/constants';
import CompletedIcon from 'assets/icons/completed-icon.svg';

type AchievementProps = {
  achievement: AchievementType;
  onClaim?: (achievementId: string) => Promise<void>;
};

const isEqual = (prev: AchievementProps, next: AchievementProps) =>
  prev.achievement.uuid === next.achievement.uuid &&
  prev.achievement.current_value === next.achievement.current_value &&
  prev.achievement.is_completed === next.achievement.is_completed &&
  prev.achievement.is_claimed === next.achievement.is_claimed;

export const Achievement = memo(({ achievement, onClaim }: AchievementProps) => {
  const { t } = useTranslation();
  const [isClaimLoading, setIsClaimLoading] = useState(false);

  // Определяем тип достижения
  const displayType = useMemo(() => {
    return achievement.reward_amount > 0 ? 'reward' : 'progress';
  }, [achievement.reward_amount]);

  // Можно ли забрать награду
  const canClaim = displayType === 'reward' && achievement.is_completed && !achievement.is_claimed;

  // Форматирование прогресса
  const progressText = useMemo(() => {
    if (achievement.target_value > 0) {
      return `${formatLargeNumber(achievement.current_value)}/${formatLargeNumber(achievement.target_value)}`;
    }
    return formatLargeNumber(achievement.current_value);
  }, [achievement.current_value, achievement.target_value]);

  // Процент выполнения для прогресс-бара
  const progressPercentage = useMemo(() => {
    if (achievement.target_value <= 0) return 0;
    return Math.min((achievement.current_value / achievement.target_value) * 100, 100);
  }, [achievement.current_value, achievement.target_value]);

  const handleClaim = async () => {
    if (!canClaim || isClaimLoading || !onClaim) return;

    try {
      setIsClaimLoading(true);
      await onClaim(achievement.uuid);
    } finally {
      setIsClaimLoading(false);
    }
  };

  // Определяем текст кнопки для reward достижений
  const getButtonText = () => {
    if (achievement.is_claimed) {
      return t('progress.claimed');
    }
    return t('progress.claim');
  };

  return (
    <div
      className={`${styles.root} ${
        achievement.target_value > 0 && achievement.is_completed && displayType === 'progress'
          ? styles.completed
          : ''
      } ${
        achievement.target_value > 0 && achievement.is_completed && displayType === 'reward'
          ? styles.rewardCompleted
          : ''
      }`}
    >
      <img src={achievement.icon} alt="" width={40} height={40} />
      <div className={styles.info}>
        <div className={styles.text}>
          <span className={styles.title}>
            {achievement.title}
            {displayType === 'reward' && (
              <div className={styles.progressText}>
                {' '}
                <span>{progressText}</span>
                <img src={TOKEN_ICONS.bobeCoin} alt="" width={12} height={12} />
              </div>
            )}
          </span>
          {displayType === 'progress' &&
            achievement.description &&
            achievement.description.trim() != '' && (
              <span className={styles.description}>{achievement.description}</span>
            )}
          {displayType === 'reward' && (
            <div className={styles.progressBarWrapper}>
              <div className={styles.progressBar}>
                <div className={styles.progressFill} style={{ width: `${progressPercentage}%` }} />
              </div>
            </div>
          )}
        </div>

        {displayType === 'progress' ? (
          // Progress achievement (без награды)
          <div className={styles.valueContainer}>
            {/* Показываем галочку только если есть target_value и достижение выполнено */}
            {achievement.target_value > 0 && achievement.is_completed && (
              <img src={CompletedIcon} alt="" width={18} height={18} />
            )}
            <span className={styles.progressValue}>{progressText}</span>
          </div>
        ) : (
          // Reward achievement (с наградой)
          <div className={styles.rewardContainer}>
            <div
              className={`${styles.rewardAmount} ${
                achievement.is_claimed
                  ? styles.claimed
                  : achievement.is_completed
                    ? styles.canClaim
                    : ''
              }`}
            >
              <span>
                {!achievement.is_claimed && '+'}
                {formatLargeNumber(achievement.reward_amount, 0)}
              </span>
              <img src={TOKEN_ICONS.donCoin} alt="" width={15} height={15} />
            </div>
            <UIButton
              onClick={handleClaim}
              disabled={!canClaim || isClaimLoading}
              size={'small2'}
              loading={isClaimLoading}
              variant={canClaim ? 'primary' : 'primary'}
              className={styles.claimButton}
            >
              {getButtonText()}
            </UIButton>
          </div>
        )}
      </div>
    </div>
  );
}, isEqual);
